import { Logger } from '../logger/index.js'
import { DurationLike, UnixDuration } from '../time/duration/UnixDuration.js'
import { LazyTransform } from './lazyPipe.js'
import { sleep } from './sleep.js'

export interface RetryParams<T> {
  fn: () => Promise<T> | T
  maxAttempts: number
  delay: DurationLike | ((attempt: number) => DurationLike)
  shouldRetry?: (error: Error) => boolean
  logger?: Logger
}

export async function retry<T>({
  fn,
  delay,
  shouldRetry: _shouldRetry,
  maxAttempts,
  logger: _logger,
}: RetryParams<T>): Promise<T> {
  const logger = _logger?.for('retry') ?? Logger.SILENT
  const shouldRetry = _shouldRetry ?? (() => true)

  let attempt = 0
  let lastError: Error | undefined
  while (true) {
    attempt++
    try {
      return await fn()
    } catch (_e) {
      const error = _e as any as Error
      lastError = error

      if (attempt >= maxAttempts) {
        break
      }

      if (!shouldRetry(error)) {
        logger.debug('Should not retry. Throwing:', error)
        throw error
      }

      const sleepDuration = delay instanceof Function ? delay(attempt) : delay

      logger.debug('Sleeping for: ', { sleepDuration, attempt })
      await sleep(sleepDuration)
    }
  }
  logger.debug('Run out of retries. Throwing:', lastError)

  throw lastError
}

function exponentialBackoff(duration: DurationLike): (attempt: number) => DurationLike {
  return (attempt) => UnixDuration.fromDurationLike(duration).times(2 ** attempt)
}

retry.exponentialBackoff = exponentialBackoff

retry.lazy = <T>(options: Omit<RetryParams<T>, 'fn'>): LazyTransform<T, T> => {
  return (fn) => () => retry({ ...options, fn })
}
